// M12.4 — Settings → Conversion events. One card per connected property
// (for MVP, only the default property). Lets the user pick which GA4
// events count as conversions in the dashboard/digest/PDF. Empty
// selection = fall back to GA4's native `keyEvents` metric.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { pushGtmEvent } from "@/lib/gtm-events";
import { cn } from "@/lib/utils";
import {
  fetchConversionEvents,
  fetchPropertyEvents,
  saveConversionEvents,
  type ConversionEventWarning,
} from "@/lib/api";

interface ConversionEventsSectionProps {
  propertyId: string | null;
  propertyName: string | null;
}

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

// Same regex + length cap enforced server-side. We mirror locally so the
// input surfaces a tighter inline error before the PUT even fires.
const EVENT_NAME_RE = /^[A-Za-z0-9_]+$/;
const EVENT_NAME_MAX_LEN = 100;

export function ConversionEventsSection({
  propertyId,
  propertyName,
}: ConversionEventsSectionProps) {
  const [distinct, setDistinct] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [manualEntries, setManualEntries] = useState<string[]>([]);
  const [manualDraft, setManualDraft] = useState("");
  const [manualDraftError, setManualDraftError] = useState<string | null>(null);
  // M12.13 — case-insensitive substring filter over the full event list.
  // Filtering is purely visual; the `selected` set keeps every currently
  // checked event regardless of whether it's visible, so narrowing the
  // list never unticks anything.
  const [filter, setFilter] = useState("");
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<ConversionEventWarning[]>([]);
  const [savedToast, setSavedToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setState({ kind: "loading" });
    try {
      const [events, current] = await Promise.all([
        fetchPropertyEvents(propertyId),
        fetchConversionEvents(propertyId),
      ]);
      setDistinct(events);
      const currentSet = new Set(current);
      // Anything currently selected that isn't in the 365d distinct list
      // is a manual entry — surface it in the manual section.
      const distinctSet = new Set(events);
      const manual = current.filter((name) => !distinctSet.has(name));
      setSelected(currentSet);
      setManualEntries(manual);
      setState({ kind: "ready" });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Failed to load events",
      });
    }
  }, [propertyId]);

  useEffect(() => {
    setDistinct([]);
    setSelected(new Set());
    setManualEntries([]);
    setWarnings([]);
    setSaveError(null);
    setSavedToast(null);
    if (propertyId) {
      load();
    } else {
      setState({ kind: "idle" });
    }
  }, [propertyId, load]);

  // SKE-3435 — drop noise events from the picker by default. The
  // GA4 distinct list often contains 30-50% noise: SQLi/XSS scanner
  // probes (`page_view'""`, `1%00` etc.), Optimizely auto-generated IDs
  // (`@@1MYW2`), platform internals (`gtm.dom`, `Fides*`,
  // `consent_mode_loaded`, `experienceExecute`), and numeric junk
  // (`114'`). None of those will ever be a meaningful conversion, but
  // they bury the actually-useful events alphabetically. A small
  // disclosure below the list lets curious users opt-in to the
  // unfiltered view.
  //
  // An event the user has explicitly checked stays visible regardless
  // of the noise filter — never hide what someone deliberately picked.
  const isNoisyEventName = (name: string): boolean => {
    if (/['"\\¿ð]|%2527|%2522|���/.test(name)) return true; // injection probes
    if (/^@@/.test(name)) return true; // Optimizely auto IDs
    if (/^(Fides|consent_|gtm\.|meter|experience|test_audit)/.test(name)) return true; // platform internals
    if (/^\d+'?$/.test(name)) return true; // numeric junk
    return false;
  };
  const [showNoisyEvents, setShowNoisyEvents] = useState(false);

  const allEntries = useMemo(() => {
    // Render distinct first, then manual entries the user added (or
    // previously saved) that aren't in the distinct list.
    const distinctSet = new Set(distinct);
    const manualOnly = manualEntries.filter((n) => !distinctSet.has(n));
    return [
      ...distinct.map((name) => ({ name, manual: false })),
      ...manualOnly.map((name) => ({ name, manual: true })),
    ];
  }, [distinct, manualEntries]);

  // Apply the noise filter — anything matching `isNoisyEventName` is
  // hidden unless `showNoisyEvents` is on, OR the event is currently
  // checked (a deliberate selection always stays visible).
  const cleanEntries = useMemo(() => {
    if (showNoisyEvents) return allEntries;
    return allEntries.filter((entry) => !isNoisyEventName(entry.name) || selected.has(entry.name));
  }, [allEntries, selected, showNoisyEvents]);
  const hiddenNoisyCount = allEntries.length - cleanEntries.length;

  // M12.13 — filtered view driven by the search input. Lowercased once
  // per render rather than per-item in the loop.
  const visibleEntries = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return cleanEntries;
    return cleanEntries.filter((entry) => entry.name.toLowerCase().includes(needle));
  }, [cleanEntries, filter]);
  const filterActive = filter.trim().length > 0;

  const warningsByEvent = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warnings) map.set(w.eventName, w.reason);
    return map;
  }, [warnings]);

  const toggleSelected = useCallback((name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    setSavedToast(null);
  }, []);

  const addManual = useCallback(() => {
    const trimmed = manualDraft.trim();
    if (!trimmed) {
      setManualDraftError("Enter an event name");
      return;
    }
    if (trimmed.length > EVENT_NAME_MAX_LEN || !EVENT_NAME_RE.test(trimmed)) {
      setManualDraftError("Letters, digits, and underscores only (max 100 chars)");
      return;
    }
    setManualDraftError(null);
    setSelected((prev) => {
      const next = new Set(prev);
      next.add(trimmed);
      return next;
    });
    setManualEntries((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    setManualDraft("");
  }, [manualDraft]);

  const handleRefresh = useCallback(async () => {
    if (!propertyId) return;
    setRefreshing(true);
    try {
      const events = await fetchPropertyEvents(propertyId);
      setDistinct(events);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't refresh event list");
    } finally {
      setRefreshing(false);
    }
  }, [propertyId]);

  const handleSave = useCallback(async () => {
    if (!propertyId) return;
    setSaving(true);
    setSaveError(null);
    setSavedToast(null);
    try {
      const result = await saveConversionEvents(propertyId, Array.from(selected));
      setWarnings(result.warnings);
      pushGtmEvent("conversion_events_configured", {
        property_id: propertyId,
        event_count: result.events.length,
      });
      setSavedToast(
        result.events.length === 0
          ? "Cleared — reports will use GA4's native key events."
          : `Saved ${result.events.length} event${result.events.length === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [propertyId, selected]);

  if (!propertyId) {
    return (
      <p className="rounded-xl bg-white p-5 text-sm text-neutral-500 ring-1 ring-neutral-950/5">
        Pick a default GA4 property first — conversion events are configured per property.
      </p>
    );
  }

  return (
    <div className="rounded-xl bg-white p-5 ring-1 ring-neutral-950/5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-display text-sm font-semibold text-neutral-950">
            {propertyName ?? "Connected property"}
          </p>
          <p className="mt-1 text-sm text-neutral-500 text-pretty">
            Pick the GA4 events that count as <strong>Custom conversions</strong> for this property.
            We'll sum their event counts on the dashboard, weekly digest, and exports. Leave
            everything unchecked to fall back to GA4's native key events.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing || state.kind === "loading"}
        >
          <i
            className={cn(
              "fa-regular text-sm",
              refreshing ? "fa-spinner fa-spin" : "fa-arrows-rotate",
            )}
            aria-hidden="true"
          />
          {refreshing ? "Refreshing…" : "Refresh list"}
        </Button>
      </div>

      {state.kind === "loading" ? (
        <div className="mt-4 h-32 animate-pulse rounded-lg bg-neutral-100" aria-busy="true" />
      ) : state.kind === "error" ? (
        <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 ring-1 ring-inset ring-red-200">
          {state.message}
        </p>
      ) : (
        <>
          {/* M12.13 — search/filter input + count suffix. Only rendered
              when there's at least one event to filter; a filter on an
              empty list is just noise. */}
          {allEntries.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <label htmlFor="conv-events-filter" className="relative flex-1 min-w-[14rem]">
                <span className="sr-only">Filter events</span>
                <i
                  className="fa-regular fa-magnifying-glass pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs text-neutral-400"
                  aria-hidden="true"
                />
                <Input
                  id="conv-events-filter"
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter events…"
                  className="pl-9 font-mono text-[13px]"
                />
                {filterActive ? (
                  <button
                    type="button"
                    onClick={() => setFilter("")}
                    aria-label="Clear filter"
                    className="absolute top-1/2 right-2 -translate-y-1/2 rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
                  >
                    <i className="fa-regular fa-xmark text-xs" aria-hidden="true" />
                  </button>
                ) : null}
              </label>
              <p className="text-xs text-neutral-500 tabular-nums">
                {filterActive
                  ? `${visibleEntries.length} of ${cleanEntries.length} events`
                  : `${cleanEntries.length} event${cleanEntries.length === 1 ? "" : "s"}`}
              </p>
            </div>
          ) : null}
          {hiddenNoisyCount > 0 ? (
            <p className="mt-2 text-xs text-neutral-500">
              Filtered out{" "}
              <span className="font-semibold text-neutral-700">{hiddenNoisyCount}</span> event
              {hiddenNoisyCount === 1 ? "" : "s"} (probe traffic, Optimizely IDs, GTM internals).{" "}
              <button
                type="button"
                onClick={() => setShowNoisyEvents((v) => !v)}
                className="text-purple-500 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
              >
                {showNoisyEvents ? "Hide" : "Show"}
              </button>
            </p>
          ) : null}
          <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-neutral-200">
            {allEntries.length === 0 ? (
              <p className="p-4 text-sm text-neutral-500">
                No events fired in the last 365 days. Add one manually below if you know the name.
              </p>
            ) : visibleEntries.length === 0 ? (
              <p className="p-4 text-sm text-neutral-500">
                No events match "<span className="font-mono">{filter.trim()}</span>". Try a shorter
                substring or clear the filter.
              </p>
            ) : (
              <ul role="list" className="divide-y divide-neutral-100">
                {visibleEntries.map((entry) => {
                  const checked = selected.has(entry.name);
                  const warning = warningsByEvent.get(entry.name);
                  return (
                    <li
                      key={entry.name}
                      className="flex items-start gap-3 px-4 py-2.5 hover:bg-neutral-50/80"
                    >
                      <input
                        id={`conv-event-${entry.name}`}
                        type="checkbox"
                        className="mt-1 size-4 cursor-pointer rounded border-neutral-300 text-purple-600 focus:ring-purple-500"
                        checked={checked}
                        onChange={() => toggleSelected(entry.name)}
                      />
                      <label
                        htmlFor={`conv-event-${entry.name}`}
                        className="flex-1 cursor-pointer text-sm"
                      >
                        <span className="font-mono text-[13px] font-medium text-neutral-800">
                          {entry.name}
                        </span>
                        {entry.manual ? (
                          <span className="ml-2 inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-600/20">
                            Manual
                          </span>
                        ) : null}
                        {warning === "not_found_in_365d" ? (
                          <span className="ml-2 text-xs text-amber-700">
                            Not found in last 365 days
                          </span>
                        ) : null}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="flex-1">
              <label
                htmlFor="manual-event-name"
                className="text-[11px] font-bold uppercase tracking-wide text-neutral-500"
              >
                Add event name manually
              </label>
              <div className="mt-1 flex gap-2">
                <Input
                  id="manual-event-name"
                  value={manualDraft}
                  onChange={(e) => {
                    setManualDraft(e.target.value);
                    setManualDraftError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManual();
                    }
                  }}
                  placeholder="e.g. purchase_completed"
                  className="flex-1 font-mono text-[13px]"
                />
                <Button type="button" variant="outline" size="sm" onClick={addManual}>
                  Add
                </Button>
              </div>
              {manualDraftError ? (
                <p className="mt-1 text-xs text-red-700">{manualDraftError}</p>
              ) : (
                <p className="mt-1 text-xs text-neutral-500">
                  For events that haven't fired in 365 days yet. We'll still save them, and show a
                  warning if none are recorded.
                </p>
              )}
            </div>
          </div>

          {saveError ? (
            <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700 ring-1 ring-inset ring-red-200">
              {saveError}
            </p>
          ) : null}
          {savedToast ? (
            <p className="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-700 ring-1 ring-inset ring-green-600/20">
              {savedToast}
            </p>
          ) : null}

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-neutral-100 pt-4">
            <p className="text-xs text-neutral-500">
              {selected.size === 0
                ? "No events selected — using GA4 key events."
                : `${selected.size} event${selected.size === 1 ? "" : "s"} selected.`}
            </p>
            <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
